import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getInstantAppData } from "../../api/endpoints.ts";
import { getSalesRollupToday } from "../../api/locationMap.ts";
import { BarChart, DonutChart, getColor, LineChart } from "../../components/chart/charts.tsx";
import { getPagePath } from "../pageRegistry.ts";
import styles from "./instantDashboard.module.scss";

/* ── Endpoint filter map ─────────────────────────────── */
const FILTER_MAP: Record<string, number> = {
  instantdashboard: 0,
  instantdashboardna: 1,
  instantdashboards: 2,
  instantdashboardcg: 3,
  instantdashboardrg: 4,
  instantdashboardcantaloupe: 5,
  instantdashboardcantaloupea: 6,
  instantdashboardcantaloupeb: 7,
  instantdashboardglobal: 0,
};

const REFRESH_MS = 10 * 60 * 1000; // 10 minutes

/** Full SEK for the KPI card, space-grouped: 494 231. */
function formatSek(n: number): string {
  return Math.round(n).toLocaleString("sv-SE");
}

/* ── Types ────────────────────────────────────────────── */
interface KioskStat {
  CountDate: string;
  LiveKiosks: number;
  LiveDoors: number;
  SoldKiosks: number;
  KiosksWithTransactions: number;
}

interface TrxStat {
  MonthlyTransactionsAVG: string;
  SecondsBetweenTransactions: string;
}

interface DailyStat {
  CountDate: string;
  DailyTransactions: number;
  KiosksWithTransactions?: number;
}

interface CustomerStat {
  Customer: string;
  LiveKiosks: number;
}

interface GrowthStat {
  Customer: string;
  Growth: number;
}

interface MonthlyStat {
  Month: string;
  MonthlyTransactionCount: number;
  MonthlyTransactionCount2: number;
}

interface LovaStat {
  Users: number;
}

interface DashboardData {
  kioskstats: KioskStat[];
  customerstats: CustomerStat[];
  trxstats: TrxStat[];
  dailystats: DailyStat[];
  lovastats: LovaStat[];
  monthlyTransactionStats: MonthlyStat[];
  kioskGrowthStats: GrowthStat[];
}

/* ── Component ────────────────────────────────────────── */

interface Props {
  pageName: string;
}

export function InstantDashboardPage({ pageName }: Props) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rollup, setRollup] = useState<{ today: number; yesterday: number; month: number } | null>(null);
  const rollupInFlightRef = useRef<ReturnType<typeof getSalesRollupToday> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  const endpointFilter = FILTER_MAP[pageName.toLowerCase()] ?? 0;
  // Only the global variant (endpoint 26) gets the live-map card; its data is
  // SuperAdmin-gated, so it never leaks onto the customer-branded variants.
  const isGlobal = pageName.toLowerCase() === "instantdashboardglobal";

  const loadData = useCallback(async () => {
    try {
      const result = await getInstantAppData(endpointFilter);
      if (result.bError) {
        setError(result.sMessage ?? "Failed to load dashboard data");
        return;
      }
      const parsed: DashboardData = typeof result.oData === "string"
        ? JSON.parse(result.oData)
        : result.oData;
      setData(parsed);
      setError(null);
    } catch (e) {
      setError(String(e));
    }
  }, [endpointFilter]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_MS);
    return () => {
      clearInterval(interval);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [loadData]);

  // Today's revenue (SEK) for the map card — from the sales-rollup collector,
  // refreshed each minute. Global (endpoint 26) only; SuperAdmin-gated server-side.
  useEffect(() => {
    if (!isGlobal) { setRollup(null); return; }
    let cancelled = false;
    const load = () => {
      const p = rollupInFlightRef.current ?? (rollupInFlightRef.current = getSalesRollupToday());
      p.then((r) => { if (!cancelled) setRollup(r ? { today: Number(r.AmountSek) || 0, yesterday: Number(r.YesterdaySek) || 0, month: Number(r.MonthSek) || 0 } : null); })
        .catch(() => { if (!cancelled) setRollup(null); })
        .finally(() => { if (rollupInFlightRef.current === p) rollupInFlightRef.current = null; });
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isGlobal, pageName]);

  if (error) {
    return <div className={styles.page}><p className={styles.error}>Error: {error}</p></div>;
  }

  if (!data) {
    return <div className={styles.page}><p className={styles.loading}>Loading dashboard...</p></div>;
  }

  const kiosk = data.kioskstats[0];
  const trx = data.trxstats[0];

  // Deployment history bar chart — month labels
  const deployData = [...data.kioskstats].reverse().map(k => ({
    label: (k.CountDate ?? "").substring(5, 7),
    value: k.LiveKiosks,
    color: "#E4002B",
    tooltip: `Sold: ${k.SoldKiosks}`,
  }));

  // Kiosks with transactions (first 10 daily stats reversed)
  const kioskTrxData = data.dailystats.slice(0, 10).reverse().map(d => ({
    label: (d.CountDate ?? "").substring(8, 10) + "'",
    value: d.KiosksWithTransactions ?? 0,
    color: "#001A72",
  }));

  // Day-of-week colors (0=Sun … 6=Sat)
  const dayColors = ["#E1261C", "#1F3A6B", "#2E6BA8", "#2F9C9C", "#4FA86B", "#E0A93D", "#E8743C"];
  const todayColor = dayColors[new Date().getDay()];
  const dailyTrxData = [...data.dailystats].reverse().map(d => {
    const dow = new Date(d.CountDate ?? "").getDay(); // 0=Sun … 6=Sat
    return {
      label: (d.CountDate ?? "").substring(8, 10) + "'",
      value: d.DailyTransactions,
      color: dayColors[dow],
    };
  });

  // Top Changes (donut)
  const growthSlices = data.kioskGrowthStats.map(g => ({
    label: g.Customer,
    value: g.Growth,
    tooltip: `${g.Growth} ${g.Customer}`,
  }));

  // Top 10 Customers (donut)
  const customerSlices = data.customerstats.map(c => ({
    label: c.Customer,
    value: c.LiveKiosks,
    tooltip: `${c.LiveKiosks} kiosks`,
  }));

  // Monthly transactions (line with current vs last year)
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const toMonthLabel = (m: string) => monthNames[parseInt(m, 10) - 1] ?? m;
  const monthlyCurrentSeries = data.monthlyTransactionStats.map(m => ({
    label: toMonthLabel(m.Month),
    value: m.MonthlyTransactionCount,
    tooltip: `${toMonthLabel(m.Month)}: ${m.MonthlyTransactionCount}`,
  }));
  const monthlyPrevSeries = data.monthlyTransactionStats.map(m => ({
    label: toMonthLabel(m.Month),
    value: m.MonthlyTransactionCount2,
    tooltip: `Same month last year: ${m.MonthlyTransactionCount2}`,
  }));

  const lovaTotal = data.lovastats[0]?.Users ?? 0;
  const lovaToday = data.lovastats[1]?.Users ?? 0;

  return (
    <div className={styles.page}>
      {/* ── Focus labels row ─────────────────── */}
      <div className={styles.metricsRow}>
        <MetricCard value={`${kiosk?.LiveDoors ?? "—"} / ${kiosk?.LiveKiosks ?? "—"}`} label="Deployed Units / POS" className={styles.metric1} />
        <MetricCard value={kiosk?.SoldKiosks} label="Delivered POS" className={styles.metric2} />
        <MetricCard value={trx?.MonthlyTransactionsAVG} label="Monthly transactions" className={styles.metric3} style={{ backgroundColor: todayColor, color: "#fff" }} />
        <MetricCard value={trx?.SecondsBetweenTransactions} label="Transaction gap (s)" className={styles.metric4} />
      </div>

      {/* ── Row 1: Bar charts (4-col, daily spans 2) ── */}
      <div className={styles.chartRow3}>
        <ChartCard title="POS deployment history">
          <BarChart data={deployData} width={550} height={400} color="#E4002B" />
        </ChartCard>
        <ChartCard title="POS with transactions">
          <BarChart data={kioskTrxData} width={550} height={400} color="#001A72" />
        </ChartCard>
        <ChartCard title="Daily transactions" span={2}>
          <BarChart data={dailyTrxData} width={1100} height={400} color="#E8E6DF" />
        </ChartCard>
      </div>

      {/* ── Row 2: Donuts, line, lova ────────── */}
      <div className={styles.chartRow}>
        <ChartCard title="Top Changes last 30 days">
          <div className={styles.donutRow}>
            <DonutChart data={growthSlices} size={220} innerRadius={0} />
            <Legend items={growthSlices} />
          </div>
        </ChartCard>
        <ChartCard title="Top 10 Customers">
          <div className={styles.donutRow}>
            <DonutChart data={customerSlices} size={220} innerRadius={0} />
            <Legend items={customerSlices} />
          </div>
        </ChartCard>
        <ChartCard title="Monthly transactions">
          <LineChart
            series={[
              { data: monthlyCurrentSeries, color: "#E4002B", width: 4 },
              { data: monthlyPrevSeries, color: "#E4002B44", width: 2 },
            ]}
            width={550}
            height={400}
            rotateLabels
          />
        </ChartCard>
        <div className={styles.lovaCard}>
          <div className={styles.lovaStack}>
            {isGlobal && (
              <button type="button" className={styles.salesToday} onClick={() => navigate(getPagePath("locationmap"))} title="Open live sales map">
                <span className={styles.salesTodayLabel}>Sales today</span>
                <span className={styles.salesTodayValue}>{rollup ? formatSek(rollup.today) : "—"}<em>SEK</em></span>
                <span className={styles.salesTodayMonth}>Yesterday <strong>{rollup ? formatSek(rollup.yesterday) : "—"}</strong> SEK</span>
                <span className={styles.salesTodayMonth}>This month <strong>{rollup ? formatSek(rollup.month) : "—"}</strong> SEK</span>
              </button>
            )}
            <div className={`${styles.lovaInner} ${isGlobal ? styles.lovaCompact : ""}`}>
              <div className={styles.lovaHeader}>Lova</div>
              <div className={styles.lovaCenter}>
                <div className={styles.lovaValue}>{String(lovaTotal).replace(/\B(?=(\d{3})+(?!\d))/g, " ")}</div>
                <div className={styles.lovaLabel}>Total users</div>
              </div>
              <div className={styles.lovaToday}>
                <svg className={styles.lovaTodayArrow} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 19V5M12 5l-6 6M12 5l6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Today +{lovaToday}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ───────────────────────────────────── */

function MetricCard({ value, label, className, style, onClick }: { value: unknown; label: string; className?: string; style?: React.CSSProperties; onClick?: () => void }) {
  const clickable = !!onClick;
  return (
    <div
      className={`${styles.metricCard} ${className ?? ""} ${clickable ? styles.metricClickable : ""}`}
      style={style}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } } : undefined}
    >
      <div className={styles.metricValue}>{value != null ? String(value) : "—"}</div>
      <div className={styles.metricLabel}>{label}</div>
    </div>
  );
}

function ChartCard({ title, children, span }: { title: string; children: React.ReactNode; span?: 2 | 4 }) {
  const spanClass = span === 4 ? styles.span4 : span === 2 ? styles.span2 : "";
  return (
    <div className={`${styles.chartCard} ${spanClass}`}>
      <div className={styles.chartTitle}>{title}</div>
      <div className={styles.chartBody}>{children}</div>
    </div>
  );
}

function Legend({ items }: { items: { label: string; value: number; color?: string }[] }) {
  return (
    <div className={styles.legend}>
      {items.map((item, i) => (
        <div key={i} className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: getColor(i, item.color) }} />
          <span>{item.label}: {item.value}</span>
        </div>
      ))}
    </div>
  );
}
